/**
 * Progress messages the server sends over the websocket while it prepares a document
 * (PDF, Word, PowerPoint or a translation).
 *
 *   { type, rfp_id, progress, message, status: 'READY' | 'PROCESSING', is_already_generated }
 *
 * When the document already exists, the server answers at once with progress 100 and
 * `is_already_generated: true` / `status: 'READY'`. The screen should then just show a short
 * notice and download, not flash a full progress dialog.
 */

export const DOCUMENT_PROGRESS_TYPES = Object.freeze(['TRANSLATION_PROGRESS', 'PPT_GENERATION_PROGRESS']);

export const isDocumentProgress = (payload) => DOCUMENT_PROGRESS_TYPES.includes(payload?.type);

/** True when the document was made earlier and nothing needs to be generated. */
export const isAlreadyGenerated = (payload) =>
    payload?.is_already_generated === true || String(payload?.status || '').toUpperCase() === 'READY';

/** The progress as a whole number from 0 to 100. */
export const readProgressPercent = (payload) => {
    const value = Number(payload?.progress);
    if (!Number.isFinite(value)) return 0;
    return Math.min(100, Math.max(0, Math.round(value)));
};

export const extractFilenameFromHeaders = (response, defaultFilename) => {
    const disposition = response?.headers?.['content-disposition'];
    if (disposition) {
        const starMatch = disposition.match(/filename\*\s*=\s*UTF-8''([^;\r\n]+)/i);
        if (starMatch?.[1]) {
            try {
                const decoded = decodeURIComponent(starMatch[1].trim());
                if (decoded) return decoded;
            } catch (_) { /* fall through */ }
        }
        // Plain form: filename="name.pdf" or filename=name.pdf
        const plainMatch = disposition.match(/filename\s*=\s*["']?([^"';\r\n]+)["']?/i);
        if (plainMatch?.[1]) {
            const name = plainMatch[1].trim();
            if (name) return name;
        }
    }
    return defaultFilename;
};
